// テキストなど「パラメータから生成される」レイヤーのラスタ結果キャッシュ (非リアクティブ、保存対象外)。
// キーはレイヤーのパラメータ JSON。表面は PixelSurface (文書座標原点)。
import { PixelSurface } from './pixelSurface'
import { surfaceFromRgba } from './imageDataUtil'
import type { TextLayer, ShapeLayer, SmartLayer } from '@/types/document'
import { resampleAffine, transformedBounds, mat3Invert } from './transform'
import { mat3Multiply, mat3Translate } from '@/types/geometry'
import { rasterizeShape } from '@/vector/rasterize'
import type { Rect } from '@/types/geometry'
import { rasterizeText } from '@/text/rasterize'
import { ensureFont } from '@/text/fonts'

interface Entry {
  kind: 'text' | 'shape' | 'smart'
  key: string
  surface: PixelSurface
  /** 表面座標 → 文書座標のオフセット */
  offset: { x: number; y: number }
  rect: Rect | null
}

const cache = new Map<string, Entry>()

function textKey(l: TextLayer): string {
  return JSON.stringify([l.text, l.style, l.position, l.boxWidth ?? null, l.warp ?? null])
}

/** 同梱フォントの読み込み待ちで代替書体になったラスタを捨てる (onFontsReady から呼ぶ) */
export function dropTextRasters(): void {
  for (const [id, e] of cache) if (e.kind === 'text') cache.delete(id)
}

/** テキストレイヤーのラスタ (無ければ生成)。空なら null */
export function textRaster(l: TextLayer): { surface: PixelSurface; offset: { x: number; y: number }; rect: Rect } | null {
  const key = textKey(l)
  const cur = cache.get(l.id)
  if (cur && cur.key === key) return cur.rect ? { surface: cur.surface, offset: cur.offset, rect: cur.rect } : null
  // 同梱フォントがまだ読み込まれていなければ、いったん代替書体で描いておき、
  // 読み込み完了 (onFontsReady) でこのキャッシュごと捨てて描き直す
  ensureFont(l.style, l.text)
  const r = l.text.length ? rasterizeText(l) : null
  if (!r) {
    cache.set(l.id, { kind: 'text', key, surface: new PixelSurface('rc_' + l.id, 4), offset: { x: 0, y: 0 }, rect: null })
    return null
  }
  const surface = surfaceFromRgba('rc_' + l.id + '_' + Date.now().toString(36), r.image.width, r.image.height, r.image.data)
  const entry: Entry = { kind: 'text', key, surface, offset: { x: r.rect.x, y: r.rect.y }, rect: r.rect }
  cache.set(l.id, entry)
  return { surface, offset: entry.offset, rect: r.rect }
}

function shapeKey(l: ShapeLayer): string {
  return JSON.stringify([l.geometry, l.fillHex, l.fillAlpha, l.strokeHex, l.strokeWidth, l.strokeAlpha, l.strokeAlign, l.transform])
}

/** シェイプレイヤーのラスタ (無ければ生成)。空なら null */
export function shapeRaster(l: ShapeLayer): { surface: PixelSurface; offset: { x: number; y: number }; rect: Rect } | null {
  const key = shapeKey(l)
  const cur = cache.get(l.id)
  if (cur && cur.key === key) return cur.rect ? { surface: cur.surface, offset: cur.offset, rect: cur.rect } : null
  const r = rasterizeShape(l)
  if (!r) {
    cache.set(l.id, { kind: 'shape', key, surface: new PixelSurface('rc_' + l.id, 4), offset: { x: 0, y: 0 }, rect: null })
    return null
  }
  const surface = surfaceFromRgba('rc_' + l.id + '_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6), r.image.width, r.image.height, r.image.data)
  const entry: Entry = { kind: 'shape', key, surface, offset: { x: r.rect.x, y: r.rect.y }, rect: r.rect }
  cache.set(l.id, entry)
  return { surface, offset: entry.offset, rect: r.rect }
}

function smartKey(l: SmartLayer): string {
  return JSON.stringify([l.sourceId, l.sourceRect, l.transform, l.interpolation])
}

/** スマートオブジェクトのラスタ: 元画像を行列で再サンプル (平行移動だけなら複製)。空なら null */
export function smartRaster(l: SmartLayer, source: PixelSurface | undefined): { surface: PixelSurface; offset: { x: number; y: number }; rect: Rect } | null {
  const key = smartKey(l)
  const cur = cache.get(l.id)
  if (cur && cur.key === key) return cur.rect ? { surface: cur.surface, offset: cur.offset, rect: cur.rect } : null
  const sr = l.sourceRect
  const m = l.transform
  let img: { width: number; height: number; data: Uint8ClampedArray } | null = null
  let rect: Rect | null = null
  if (source && sr.w > 0 && sr.h > 0) {
    const translationOnly = m[0] === 1 && m[1] === 0 && m[3] === 0 && m[4] === 1 && Number.isInteger(m[6]) && Number.isInteger(m[7])
    if (translationOnly) {
      img = { width: sr.w, height: sr.h, data: source.readRect(sr) }
      rect = { x: sr.x + m[6], y: sr.y + m[7], w: sr.w, h: sr.h }
    } else {
      const inv = mat3Invert(m)
      if (inv) {
        const bounds = transformedBounds(sr, m)
        if (bounds.w > 0 && bounds.h > 0) {
          const src = { width: sr.w, height: sr.h, data: source.readRect(sr) }
          img = resampleAffine(src, bounds, mat3Multiply(mat3Translate(-sr.x, -sr.y), inv), l.interpolation)
          rect = bounds
        }
      }
    }
  }
  if (!img || !rect) {
    cache.set(l.id, { kind: 'smart', key, surface: new PixelSurface('rc_' + l.id, 4), offset: { x: 0, y: 0 }, rect: null })
    return null
  }
  const surface = surfaceFromRgba('rc_' + l.id + '_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6), img.width, img.height, img.data)
  const entry: Entry = { kind: 'smart', key, surface, offset: { x: rect.x, y: rect.y }, rect }
  cache.set(l.id, entry)
  return { surface, offset: entry.offset, rect }
}

export function dropRaster(layerId: string): void {
  cache.delete(layerId)
}

export function retainRasters(ids: Set<string>): void {
  for (const id of [...cache.keys()]) if (!ids.has(id)) cache.delete(id)
}
